import { find } from "lodash";
import { ReactNode } from "react";
import { colors, graph, system } from "@/theme";

import { countDecimals, moment, roundToMaxDigitsAfterDot } from "@/common/util";
import { Tooltip } from "@/components";
import Icon from "@imminently/decisively-ui/Icon";
import TemporalValue from "@imminently/decisively-ui/TemporalValue";

import { getNodeResult, getValueAtDate, GraphNode, ValueType } from "@packages/commons";

import { GraphNodeType } from "./utils";
import { Index } from "@/components/GraphVisualisation/components/AttributeInfo/AttributeInfo";
import { toTitleCase } from "@/components/util/UtilString";
import filter from "lodash/filter";

export const getAttributeIconAsComponentType = (attribTyp: ValueType, defaultRet?: any) => {
  switch (attribTyp as string || "") {
    case "datetime": return ((p: any) => <Icon name="calendar-time" {...p} />);
    case "date": return ((p: any) => <Icon name="date-range" {...p} />);
    case "time": return ((p: any) => <Icon name="clock" {...p} />);
    case "text": return ((p: any) => <Icon name="text-field" {...p} />);
    case "typography": return ((p: any) => <Icon name="typography" {...p} />);
    case "boolean": return ((p: any) => <Icon name="checkbox" {...p} />);
    case "currency": return ((p: any) => <Icon name="currency" {...p} />);
    case "options": return ((p: any) => <Icon name="boolean" {...p} />); // badly named but it looks good
    case "entity": return ((p: any) => <Icon name="implementation" {...p} />);
    case "radio": return ((p: any) => <Icon name="radio-button" {...p} />);
    case "number": return ((p: any) => <Icon name="level-1" {...p} />);
    case "number_of_instances": return ((p: any) => <Icon name="functions" {...p} />);
    case "auto": return ((p: any) => <Icon name="auto" {...p} />);
    default:
      return (defaultRet || null);
  }
};

export const getAttributeIcon = (type: ValueType, defaultIcon: any = ((p: any) => <Icon name="warning" {...p} />)) => {
  switch (type as string ?? "") {
    case "datetime": return ((p: any) => <Icon name="calendar-time" {...p} />);
    case "date": return ((p: any) => <Icon name="date-range" {...p} />);
    case "time": return ((p: any) => <Icon name="clock" {...p} />);
    case "text": return ((p: any) => <Icon name="text-field" {...p} />);
    case "typography": return ((p: any) => <Icon name="typography" {...p} />);
    case "boolean": return ((p: any) => <Icon name="checkbox" {...p} />);
    case "currency": return ((p: any) => <Icon name="currency" {...p} />);
    case "options": return ((p: any) => <Icon name="boolean" {...p} />); // badly named but it looks good
    case "entity": return ((p: any) => <Icon name="implementation" {...p} />);
    case "radio": return ((p: any) => <Icon name="radio-button" {...p} />);
    case "number": return ((p: any) => <Icon name="level-1" {...p} />);
    case "number_of_instances": return ((p: any) => <Icon name="functions" {...p} />);
    case "auto": return ((p: any) => <Icon name="auto-generate-pen" {...p} />);
    default:
      return defaultIcon;
  }
};

export type AttributeIconProps = {
  type: ValueType;
  nodeType?: GraphNodeType
} & React.HTMLAttributes<SVGElement>;

/**
 * A simple component wrapper over getAttributeIcon.
 * NOTE if you want a tooltip, you will need to wrap this component
 */
export const AttributeIcon = ({ type, nodeType, ...props }: AttributeIconProps) => {
  if (nodeType === GraphNodeType.VALIDATION) {
    return <Icon name="warning" {...props} />
  }

  const IconComponent = getAttributeIcon(type);
  return <IconComponent {...props} />;
};

export const getGraphNodeTypeColor = (type: GraphNodeType) => {
  switch (type) {
    case GraphNodeType.INPUT: return system.purpleSlate;
    case GraphNodeType.DERIVED: return system.orangeFiesta;
    case GraphNodeType.GOAL: return system.greenTea;
    case GraphNodeType.IDENTIFIER: return system.blueLavendar;
    case GraphNodeType.VALIDATION: return system.redBlush;
    default:
      return colors.hoverGrey;
  }
};

export const NodeDisplayType = {
  /** undefined */
  UNKNOWN: 'Unknown',
  /** null */
  UNCERTAIN: 'Uncertain',
  TEMPORAL: 'Temporal',
  BOOLEAN: 'Boolean',
  STRING: 'String',
  NUMBER: 'Number',
  ENTITY: 'Entity',
  FILE: 'File',
  DATE: 'Date',
  TEST_OUTCOME: "TestOutcome"
} as const;

export type TNodeDisplayType = typeof NodeDisplayType[keyof typeof NodeDisplayType];

export interface TemporalTextProps {
  maxEntries?: number;
  scrollable?: boolean;
}

export const getNodeDisplayData = (node: GraphNode, temporalSlice = undefined, opts: {
  temporalTextProps?: TemporalTextProps;
} = {}) => {
  // allow us to override the value as we might be able to cast he value
  let value = getNodeResult(node);
  // default to unknown
  let type: TNodeDisplayType = NodeDisplayType.UNKNOWN;
  let display: ReactNode = NodeDisplayType.UNKNOWN;
  let tooltip: string = "";
  let color: string = graph.unknownAllTime.dark;
  let backgroundColor: string = graph.unknownAllTime.light;
  // const icon = getAttributeIcon(node.value.type);

  // if value is undefined, the defaults are correct
  if (typeof value !== 'undefined') {
    if (value === null) {
      display = NodeDisplayType.UNCERTAIN;
      type = NodeDisplayType.UNCERTAIN;
    }
    else if (value === "pass" || value === "fail") {
      display = toTitleCase(value);
      type = NodeDisplayType.TEST_OUTCOME;
    } else if (typeof value === 'string') {
      const valueLower = value.toLowerCase();
      if (valueLower === "true" || valueLower === "false") {
        // is actually boolean, convert it
        value = valueLower === "true";
        display = String(value);
        type = NodeDisplayType.BOOLEAN;
      } else if (node.type === "date") {
        display = value.replace(/T.*$/, "");
        type = NodeDisplayType.DATE;
      } else if (value.startsWith('data:')) {
        // normalise it into this format
        value = { fileRefs: [value] };
        display = "File"; // we won't use this, as we need the filename, but don't keep it as unknown
        type = NodeDisplayType.FILE;
      } else {
        display = value;
        type = NodeDisplayType.STRING;
      }
    } else if (typeof value === 'number') {
      display = roundToMaxDigitsAfterDot(value);
      type = NodeDisplayType.NUMBER;
      if (countDecimals(value) > 4) {
        tooltip = String(value);
      }
    } else if (value.temporal) {
      type = NodeDisplayType.TEMPORAL;
      if (temporalSlice) {
        // @ts-ignore
        display = getValueAtDate(value.temporal, temporalSlice);
        tooltip = `Displaying temporal value at ${moment(temporalSlice).format('YYYY-MM-DD')}`
      } else {
        display = <TemporalValue node={node} value={value} textProps={opts.temporalTextProps} />;
      }
    } else if (Array.isArray(value.fileRefs)) {
      display = "File"; // we won't use this, as we need the filename, but don't keep it as unknown
      type = NodeDisplayType.FILE;
    } else if (typeof value === "boolean") {
      display = String(value);
      type = NodeDisplayType.BOOLEAN;
    } else {
      display = String(value);
      type = NodeDisplayType.ENTITY;
    }
  }

  switch (type as TNodeDisplayType) {
    case NodeDisplayType.BOOLEAN:
      color = value ? graph.pass.darkText : graph.fail.darkText;
      backgroundColor = value ? graph.pass.light : graph.fail.light;
      break;
    case NodeDisplayType.UNCERTAIN:
      color = graph.uncertain.dark;
      backgroundColor = graph.uncertain.light;
      break;
    case NodeDisplayType.UNKNOWN:
      color = graph.unknownAllTime.dark;
      backgroundColor = graph.unknownAllTime.light;
      break;
    case NodeDisplayType.TEST_OUTCOME:
      color = value === "pass" ? graph.pass.darkText : graph.fail.darkText;
      backgroundColor = value === "pass" ? graph.pass.light : graph.fail.light;
      break;
    default:
      // certain, everything else
      color = colors.kindaBlack;
      backgroundColor = graph.certain.light;
      break;
  }

  return { value, type, display, tooltip, color, backgroundColor };
};

export const getIndexForNode = (node, graph) => {
  const index = node.index;//typeof node.index !== 'undefined' ? Number.parseInt(node.index) : undefined;
  if (node._indexData) {
    return node._indexData;
  }
  if (typeof index === "undefined" || !graph) return;
  const identifierNodes = graph._identifierNodes || (graph._identifierNodes = filter(graph._nodes, n => n && n.identifier));

  // See if there is a value for this identifier in the graph
  let identifier = identifierNodes.find((n) => {
    if (n && n.identifier && node.entity === n.entity && node.index == n.index) {
      // Check pathing
      if (node.parent_path) {
        if (n.parent_path === node.parent_path) return true;
        else return false;
      }
      return true;
    }
    return false;
  })
  if (identifier && getNodeResult(identifier) !== undefined) {
    return node._indexData = {
      value: getNodeResult(identifier),
      hint: index,
      identifier: identifier.description
    }
  }
  return node._indexData = {
    value: index,
    identifier: identifier?.description
  }
};

export const getIndexForNodeTooltip = ({ hint, identifier, value }) => {
  let tooltip = `Index: ${hint}`;
  if (identifier) tooltip += ` (${identifier})`;
  return tooltip;
};

export const displayNodeIndexInline = (node, graph, opts: any = {}) => {
  let indexInfo = getIndexForNode(node, graph);
  if (!indexInfo) return '';

  if (typeof indexInfo.hint !== "undefined") {
    return (
      <Tooltip title={getIndexForNodeTooltip(indexInfo as any)}>
        <span>{opts.inBracket ? '(' : ''}{indexInfo.hint}{opts.inBracket ? ')' : ''}</span>
      </Tooltip>
    )
  } else return `${opts.inBracket ? '(' : ''}${node.index}${opts.inBracket ? ')' : ''}`;
};

export const getNodeLabel = (node, indexInfo?) => {
  const index = typeof node.index !== 'undefined' ? Number.parseInt(node.index) : undefined;

  return (
    <span>{node.description}{typeof index !== "undefined" ? <span> ({indexInfo ? <Index {...indexInfo} /> : index})</span> : null}</span>
  );
};

export const getContainedBy = (path: string, graph?: any) => {
  if (!path) return '';
  let els = path.split('/');
  els.pop(); // Remove the attribute
  if (els.length === 0) {
    // This shouldn't happen - the path only had the attribute
    return;
  } else if (els.length <= 2) {
    // It's an entity/index path. It's not contained by anything so return nothing
    return;
  } else {
    els.pop(); // Pop off the index
    els.pop(); // Pop off the entity
    // We have to try and work out the identifiers
    let hasIdentifier = false;
    const cleaned = els.map((el, index) => {
      if (index % 2) {
        // Check if this instance should be referenced by the identifier
        let info = getIndexForNode({
          entity: els[index - 1],
          index: el,
          parent_path: els.length > 2 ? els.slice(0, index - 2).join('/') : undefined
        }, graph)
        if (info && info.hint) {
          hasIdentifier = true;
          return info.value;
        } else return el;
      } else {
        // It's an entity
        // No need to clean these
        return el;
      }
    })

    return `Contained by: ${cleaned.join('/')} ${hasIdentifier ? `(${els.join('/')})` : ''}`;
  }
};

export const getVisualPath = (path: string, graph: any) => {
  if (!path || path === 'global') return '';
  let els = path.split('/');
  els.pop(); // Remove the attribute
  if (els.length === 0) {
    // This shouldn't happen - the path only had the attribute
    return '';
  }
  const cleaned = els.map((el, index) => {
    if (index % 2) {
      let info = getIndexForNode({ entity: els[index - 1], index: el, parent_path: (index > 1 && els.length > 2) ? els.slice(0, index - 1).join('/') : undefined }, graph);
      // @ts-ignore
      return <Index {...info} />
    } else {
      return index === 0 ? `${el}/` : `/${el}/`;
    }
  });
  return cleaned;
}